import { useState, useEffect, useCallback } from "react";
import { universityService } from "../services/api/universityService";
import { University } from "../types/university";

interface UsePopularUniversitiesReturn {
  universities: University[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePopularUniversities = (): UsePopularUniversitiesReturn => {
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPopularUniversities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await universityService.getPopularUniversities();

      if (response.data && response.data.success && response.data.data) {
        setUniversities(response.data.data.universities || []);
      } else {
        setError(
          response.data?.message || "Failed to fetch popular universities"
        );
      }
    } catch (err) {
      console.error("Error fetching popular universities:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPopularUniversities();
  }, [fetchPopularUniversities]);

  const refetch = () => {
    fetchPopularUniversities();
  };

  return {
    universities,
    loading,
    error,
    refetch,
  };
};

interface UseUniversitiesReturn {
  universities: University[];
  loading: boolean;
  error: string | null;
  pagination: any;
  refetch: (filters?: {
    search?: string;
    city?: string;
    sortBy?: string;
    page?: number;
    limit?: number;
  }) => void;
}

export const useUniversities = (initialFilters?: {
  search?: string;
  city?: string;
  sortBy?: string;
  page?: number;
  limit?: number;
}): UseUniversitiesReturn => {
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<any>(null);

  const fetchUniversities = useCallback(
    async (filters?: {
      search?: string;
      city?: string;
      sortBy?: string;
      page?: number;
      limit?: number;
    }) => {
      try {
        setLoading(true);
        setError(null);

        const page = filters?.page || initialFilters?.page || 1;
        const limit = filters?.limit || initialFilters?.limit || 20;

        const filterParams: any = {};

        if (filters?.search || initialFilters?.search) {
          filterParams.search = filters?.search || initialFilters?.search;
        }

        if (filters?.city || initialFilters?.city) {
          filterParams.city = filters?.city || initialFilters?.city;
        }

        if (filters?.sortBy || initialFilters?.sortBy) {
          filterParams.sortBy = filters?.sortBy || initialFilters?.sortBy;
        }

        const response = await universityService.getAllUniversities(
          page,
          limit,
          filterParams
        );

        if (response.data && response.data.success && response.data.data) {
          setUniversities(response.data.data.universities || []);
          setPagination(response.data.data.pagination || null);
        } else {
          setError(response.data?.message || "Failed to fetch universities");
        }
      } catch (err) {
        console.error("Error fetching universities:", err);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    },
    [initialFilters]
  );

  useEffect(() => {
    fetchUniversities();
  }, [fetchUniversities]);

  const refetch = (filters?: {
    search?: string;
    city?: string;
    sortBy?: string;
    page?: number;
    limit?: number;
  }) => {
    fetchUniversities(filters);
  };

  return {
    universities,
    loading,
    error,
    pagination,
    refetch,
  };
};

export default useUniversities;
